import { useCallback, useState } from 'react';
import { Button, Flex, SearchInput } from '@optiaxiom/react';

import {
  useAppCompanionRuleCreate,
  useAppCompanionRuleDelete,
  useAppCompanionRuleList,
  useAppCompanionRuleUpdate,
  useCompanionRulesUrlParams,
  useDebouncedSearch,
} from '@frontend/hooks';
import { CompanionRule, CreateRuleRequest, UpdateRuleRequest } from '@frontend/types/app-companion';

import { CompanionRulesTable, RuleDialog, RuleExecutionsSidebar } from './components';

type AppCompanionTabProps = {
  agentId: string;
};

export function AppCompanionTab({ agentId }: AppCompanionTabProps) {
  const [addRuleDialogOpen, setAddRuleDialogOpen] = useState(false);
  const [editRuleDialogOpen, setEditRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<CompanionRule | null>(null);
  const [selectedRule, setSelectedRule] = useState<CompanionRule | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { page, pageSize, search, setPage, setPageSize, setSearch } = useCompanionRulesUrlParams();

  const { searchInput, handleSearchChange } = useDebouncedSearch({
    initialValue: search,
    delay: 500,
    onSearchChange: setSearch,
  });

  // Use hooks for data fetching and mutations
  const {
    data: companionRulesResponse,
    isLoading: isLoadingRules,
    error: rulesError,
  } = useAppCompanionRuleList({
    agentId,
    page,
    pageSize,
    searchQuery: search,
  });

  const companionRules = companionRulesResponse?.data || [];
  const pagination = companionRulesResponse?.pagination;

  const createRuleMutation = useAppCompanionRuleCreate(agentId, () => {
    setAddRuleDialogOpen(false);
  });

  const updateRuleMutation = useAppCompanionRuleUpdate(agentId, editingRule?.id, () => {
    setEditRuleDialogOpen(false);
    setEditingRule(null);
  });

  const deleteRuleMutation = useAppCompanionRuleDelete(agentId);

  const handleDeleteRule = async (ruleId: string) => {
    deleteRuleMutation.mutate(ruleId);
  };

  // Handle editing a companion rule
  const handleEditRule = useCallback((rule: (typeof companionRules)[number]) => {
    if (rule) {
      setEditingRule(rule);
      setEditRuleDialogOpen(true);
    }
  }, []);

  // Handle viewing a companion rule in sidebar
  const handleViewRule = useCallback((rule: CompanionRule) => {
    setSelectedRule(rule);
    setSidebarOpen(true);
  }, []);

  const handleSubmitRule = async (ruleData: CreateRuleRequest | UpdateRuleRequest) => {
    if (editingRule) {
      updateRuleMutation.mutate(ruleData);
    } else {
      createRuleMutation.mutate(ruleData as CreateRuleRequest);
    }
  };

  return (
    <Flex flexDirection="column" gap="16" py="24">
      <Flex flexDirection="row" justifyContent="space-between" gap="16">
        <SearchInput
          placeholder="Search rules..."
          value={searchInput}
          onChange={e => handleSearchChange(e.target.value)}
        />
        <Button appearance="primary" size="lg" onClick={() => setAddRuleDialogOpen(true)}>
          Add Rule
        </Button>
      </Flex>

      <CompanionRulesTable
        rules={companionRules}
        pagination={pagination}
        isLoading={isLoadingRules || deleteRuleMutation.isPending}
        error={rulesError}
        onDeleteRule={handleDeleteRule}
        onEditRule={handleEditRule}
        onViewRule={handleViewRule}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      {/* Add Rule Dialog */}
      <RuleDialog
        open={addRuleDialogOpen}
        onOpenChange={setAddRuleDialogOpen}
        onSubmit={handleSubmitRule}
        agentId={agentId}
      />

      {/* Edit Rule Dialog */}
      <RuleDialog
        open={editRuleDialogOpen}
        onOpenChange={setEditRuleDialogOpen}
        onSubmit={handleSubmitRule}
        agentId={agentId}
        editRule={editingRule}
      />

      {/* Rule Executions Sidebar */}
      {selectedRule && (
        <RuleExecutionsSidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          rule={selectedRule}
          agentId={agentId}
        />
      )}
    </Flex>
  );
}
